import { useEffect, useState, useRef, useContext } from 'react';
import { MapContext, NavBarContext } from '../../App';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import classes from "./NavBar.module.css";
import SignIn from '../SignIn';
import Register from '../Register';
import NavBarSearch from './NavBarSearch/NavBarSearch';
import ProfileMenu from './ProfileMenu';
import HelpMenu from './HelpMenu';
import { clearRedirectPathIfMatches, storeRedirectPathTo } from '../../util/redirectUtils';
import { useHasStudios } from '../../hooks/useHasStudios';
import useUnreadMessagesCount from '../../hooks/messages/useUnreadMessagesCount';
import useBookingNotifications from '../../hooks/useBookingNotifications';
import { useHelpMenuTrigger } from '../../hooks/useHelpMenuTrigger';
import { isHelpMenuPanelTarget } from '../../util/isHelpMenuPanelTarget';

const NavBar = ({ 
    fromStudio = false, 
    fromSearch = false, 
    fromProfile = false, 
    fromUserProfile = false, 
    fromBookings = false, 
    fromMessages = false,
  }) => {
    
  const { status, isAuthenticated, user } = useSelector((state) => state?.auth);
  const { profilePicture } = user;

  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  const dropdownRef = useRef(null);
  const burgerMenuRef = useRef(null);
  const userContainerRef = useRef(null);
  const helpMenu = useHelpMenuTrigger({ hoverCloseDelay: 300 });
  const burgerHelpMenu = useHelpMenuTrigger({ hoverCloseDelay: 300 });

  const { hideTopPage } = useContext(MapContext);
  const { showSignIn, setShowSignIn, showRegister, setShowRegister, isPhoneSize, activeView, setActiveView } = useContext(NavBarContext);

  const { hasStudios } = useHasStudios(user?.id);
  const unreadMessagesCount = useUnreadMessagesCount();
  
  // Only fetch booking notifications for hosts (creator mode)
  // Guests (explorer mode) don't need booking notifications - they're waiting for host to respond
  const isHost = hasStudios || activeView === 'creator';
  const bookingNotifications = useBookingNotifications(isHost ? activeView : null);

  const isAuthLoading = status === "loading" && !isAuthenticated;
  
  // Check if there are any notifications:
  // - Always show for unread messages (both host and guest)
  // - Only show booking notifications for hosts (they need to answer booking requests)
  // - Guests don't see booking notifications (they're waiting for host to respond)
  const hasNotifications = (unreadMessagesCount > 0) || (isHost && bookingNotifications > 0);
      
  useEffect(() => {
    if (
        location.pathname === '/' 
        || location.pathname === '/bookings'
      ) {
      setActiveView('explorer');
    } else if (
        location.pathname === '/services-studios' 
        || location.pathname.startsWith('/services-studios/')
        || location.pathname === '/creator'
        || location.pathname.startsWith('/creator/')
        || location.pathname === '/calendar'
        || location.pathname === '/studio-erstellen'
        || location.pathname.startsWith('/studio-erstellen/')
      ) {
      setActiveView('creator');
    }
    // Note: /messages routes no longer automatically switch to creator mode
    // This allows users to stay in their current view (explorer or creator)
  }, [location.pathname, setActiveView]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isHelpMenuPanelTarget(event.target)) {
        return;
      }

      if (
        profileMenuRef?.current && 
        !profileMenuRef?.current?.contains(event?.target) &&
        !userContainerRef?.current?.contains(event?.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef?.current && !dropdownRef?.current?.contains(event?.target) &&
        burgerMenuRef?.current && !burgerMenuRef?.current?.contains(event?.target)
      ) {
        setShowDropdown(false);
      }

    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignInClick = () => {
    setShowSignIn(true);
    setShowRegister(false);
    setShowDropdown(false);
  };

  const clearCurrentBookingRedirect = () => {
    const params = new URLSearchParams(location.search);
    params.set('booking', 'open');
    const search = params.toString();

    clearRedirectPathIfMatches({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
      hash: location.hash,
    });
  };

  const handleCloseSignIn = () => {
    clearCurrentBookingRedirect();
    setShowSignIn(false);
  };

  const handleRegisterClick = () => {
    setShowRegister(true);
    setShowSignIn(false);
    setShowDropdown(false);
  };

  const handleCloseRegister = () => {
    clearCurrentBookingRedirect();
    setShowRegister(false);
  };

  const handleSwitchToRegister = () => {
    setShowSignIn(false);
    setShowRegister(true);
  };

  const handleSwitchToSignIn = () => {
    setShowRegister(false);
    setShowSignIn(true);
  };

  const handleBurgerMenuClick = () => {
    setShowDropdown((prev) => !prev);
  };

  const handleProfileMenuClick = () => {
    setShowProfileMenu(prev => !prev);
  };

  const handleGastgeberClick = () => {
    if(isAuthenticated){

      navigate('/studio-erstellen');
    } else {
      // Make sure OAuth redirect returns to studio creation
      storeRedirectPathTo('/studio-erstellen');
      
      setShowSignIn(false);
      setShowRegister(true);
    }
  };

  return (
    <>
      <header className={classes.header}>
        <div className={classes.insideContainer}>
          <div className={classes.firstContainer}>
            <Link to="/" className={classes.navigationLinks}>
              <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
              </svg>
            </Link>
            {(hideTopPage || fromSearch || fromBookings) && !fromMessages && !fromProfile && (
              <NavBarSearch isAuthenticated={isAuthenticated}/>
            )}
            {!(fromSearch || fromBookings || fromStudio) && (
              <>
                <div className={classes.smallLine}></div>
                <Link target="_blank" rel="noopener noreferrer" to={"https://stu-studios.de/info/"}>So funktioniert Stu</Link>
              </>
            )}
            <div className={`${classes.smallLine} ${(hideTopPage || fromSearch || fromBookings) ? classes.hidden : ''}`}></div>
            <div
              ref={helpMenu.containerRef}
              className={`${classes.helpTrigger} ${helpMenu.isActive ? classes.helpTriggerActive : ''} ${hideTopPage && !fromMessages ? classes.hidden : ''} ${fromSearch || fromBookings ? classes.hidden : ''}`}
              onMouseEnter={helpMenu.onMouseEnter}
              onMouseLeave={helpMenu.onMouseLeave}
              onClick={helpMenu.onClick}
            >
              <span>Hilfe</span>
              <HelpMenu
                variant="dropdown"
                panelRef={helpMenu.panelRef}
                open={helpMenu.isOpen}
                onClose={helpMenu.close}
                onMouseEnter={helpMenu.onMouseEnter}
                onMouseLeave={helpMenu.onMouseLeave}
              />
            </div>
          </div>
          <div className={`${classes.buttonsContainer} ${!isAuthenticated && !isAuthLoading && (fromSearch || fromBookings || fromStudio || fromUserProfile) ? classes.hidden : ''}`}>
              {(!isAuthenticated && !isAuthLoading) && (
                <div className={`${(fromSearch || fromBookings) ? classes.hidden : ''}`}> 
                  <div onClick={handleGastgeberClick} className={classes.becomeHostButton}>
                    <span className="regularSmall">Gastgeber werden</span>
                  </div>
                </div>
              )}
            <div className={`${classes.loginRegisterButtons}`} style={{gap: isAuthenticated ? '20px' : '.5rem', }}>
              {isAuthenticated && !isAuthLoading && (
                <>
                {hasStudios ? (
                  <Link 
                    to={activeView === 'creator' ? "/" : "/creator"} 
                    onClick={() => setActiveView(activeView === 'creator' ? 'explorer' : 'creator')}
                  >
                    {/* <div className={classes.creatorView}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="23" height="22" viewBox="0 0 23 22" fill="none">
                        <path d="M8.61558 10.4297L4.91598 6.65407L4.48521 6.21484L4.06288 6.65407L0.363281 10.4381" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                        <path d="M14.3828 10.6289L18.0824 14.413L18.5132 14.8437L18.9355 14.413L22.6436 10.6289" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                        <path d="M4.48438 6.77344V11.0812C4.48438 16.0055 9.27358 19.9923 15.1862 19.9923" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                        <path d="M18.4722 14.674L18.5651 10.6618C18.5651 5.577 13.7674 1.45508 7.86328 1.45508" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                      </svg>
                      <span className="slimRegularLargeText">Anbieter Ansicht</span>
                    </div> */}
                    <div className={classes.switch}>
                      {activeView === 'creator' ? (
                        <>
                          <span className={`slimRegularLargeText ${classes.hoverState}`}>Entdecker</span>
                          <div className={classes.creatorViewContainer}>
                            <span className="slimRegularLargeText">Anbieter</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={classes.creatorViewContainer}>
                            <span className="slimRegularLargeText">Entdecker</span>
                          </div>
                          <span className={`slimRegularLargeText ${classes.hoverState}`}>Anbieter</span>
                        </>
                      )}
                    </div>
                  </Link>
                ): (
                  <div onClick={handleGastgeberClick} className={classes.becomeHostButton}>
                    <span className="regularSmall">Gastgeber werden</span>
                  </div>
                )}
                  <div className={classes.userWrapper}>
                    {hasNotifications && (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="13" 
                        height="13" 
                        viewBox="0 0 13 13" 
                        fill="none"
                        style={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          zIndex: 10
                        }}
                      >
                        <path 
                          d="M6.5 0.75C9.67573 0.75 12.25 3.32438 12.25 6.5C12.25 9.6756 9.67571 12.25 6.5 12.25C3.32448 12.25 0.750019 9.67561 0.75 6.5C0.75 3.32436 3.32446 0.75 6.5 0.75Z" 
                          fill="var(--Stu-Rot, #DF315A)" 
                          stroke="var(--Black, #000)" 
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                    <div 
                      ref={userContainerRef} 
                      onClick={handleProfileMenuClick} 
                      className={classes.userContainer}
                      style={{ position: 'relative' }}
                    >
                      <div className={classes.userImageContainer}>
                        <img src={profilePicture} alt="User Profile" /> 
                      </div>
                    </div>
                  </div>
                </>
              )}
              {(!isAuthenticated && !isAuthLoading) && (
                <>
                  <button className={`${classes.loginButton} ${fromSearch || fromBookings ? classes.hidden : ''}`} onClick={handleSignInClick}>Anmelden</button>
                  <button className={`${classes.registerButton} ${fromSearch || fromBookings ? classes.hidden : ''}`} onClick={handleRegisterClick}>Registrieren</button>
                </>
              )}
              {isAuthLoading && !isAuthenticated && (
                <div className={classes.loadingState}>
                  <div className={classes.skeletonSwitchContainer}>
                    <div className={classes.skeletonSwitch}></div>
                  </div>
                  <div className={classes.skeletonUserContainerOutside}>
                    <div className={classes.skeletonUserContainer}></div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {isAuthenticated && !isAuthLoading && (fromProfile || fromSearch || fromStudio || fromUserProfile || fromBookings) && (
              <div className={classes.phoneUserContainer} style={{gap: isAuthenticated ? '20px' : '.5rem', }}>
                <Link 
                  to={activeView === 'creator' ? "/" : "/creator"} 
                  onClick={() => setActiveView(activeView === 'creator' ? 'explorer' : 'creator')}
                >
                  {/* <div className={classes.creatorView}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="22" viewBox="0 0 23 22" fill="none">
                      <path d="M8.61558 10.4297L4.91598 6.65407L4.48521 6.21484L4.06288 6.65407L0.363281 10.4381" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                      <path d="M14.3828 10.6289L18.0824 14.413L18.5132 14.8437L18.9355 14.413L22.6436 10.6289" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                      <path d="M4.48438 6.77344V11.0812C4.48438 16.0055 9.27358 19.9923 15.1862 19.9923" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                      <path d="M18.4722 14.674L18.5651 10.6618C18.5651 5.577 13.7674 1.45508 7.86328 1.45508" stroke="#DEE1E5" strokeWidth="0.9" strokeMiterlimit="10"/>
                    </svg>
                    <span className="slimRegularLargeText">Anbieter Ansicht</span>
                  </div> */}
                  <div className={classes.switch}>
                    {activeView === 'creator' ? (
                      <>
                        <span className={`slimRegularLargeText ${classes.hoverState}`}>Entdecker</span>
                        <div className={classes.creatorViewContainer}>
                          <span className="slimRegularLargeText">Anbieter</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={classes.creatorViewContainer}>
                          <span className="slimRegularLargeText">Entdecker</span>
                        </div>
                        <span className={`slimRegularLargeText ${classes.hoverState}`}>Anbieter</span>
                      </>
                    )}
                  </div>
                </Link>
                <div className={classes.userWrapper}>
                      {hasNotifications && (
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="13" 
                          height="13" 
                          viewBox="0 0 13 13" 
                          fill="none"
                          style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            zIndex: 10
                          }}
                        >
                          <path 
                            d="M6.5 0.75C9.67573 0.75 12.25 3.32438 12.25 6.5C12.25 9.6756 9.67571 12.25 6.5 12.25C3.32448 12.25 0.750019 9.67561 0.75 6.5C0.75 3.32436 3.32446 0.75 6.5 0.75Z" 
                            fill="var(--Stu-Rot, #DF315A)" 
                            stroke="var(--Black, #000)" 
                            strokeWidth="1.5"
                          />
                        </svg>
                      )}
                  <div 
                      ref={userContainerRef} 
                      onClick={handleProfileMenuClick} 
                      className={classes.userContainer}
                      style={{ position: 'relative' }}
                    >
                      <div className={classes.userImageContainer}>
                        <img src={profilePicture} alt="User Profile" /> 
                      </div>
                  </div>
                </div>
              </div>
          )}
        </div>
        {(!isAuthenticated && !isAuthLoading) && (
          <div
            className={`${classes.burgerMenu} ${(hideTopPage || fromSearch || fromBookings || fromStudio || fromUserProfile) ? classes.visible : ''}`}
            onClick={handleBurgerMenuClick}
            ref={burgerMenuRef}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="19" viewBox="0 0 25 19" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M24.0371 2.30679H0.0371094V0.885742H24.0371V2.30679ZM24.0371 10.5963H0.0371094V9.17521H24.0371V10.5963ZM24.0371 18.8857H0.0371094V17.4647H24.0371V18.8857Z" fill="#F0F1F4" />
            </svg>
          </div>
        )}
        {showDropdown && (hideTopPage || fromSearch || fromBookings || fromUserProfile || fromStudio) && !isAuthenticated && !isAuthLoading && (
          <div ref={dropdownRef} className={classes.dropdownMenu}>
            <span className='regularMiddle' onClick={handleSignInClick}>Anmelden</span>
            <span className='regularMiddle' onClick={handleRegisterClick}>Registrieren</span>
            <div className={classes.smallLineHorizontal}></div>
            <span style={{cursor: 'pointer'}} onClick={handleGastgeberClick} className='regularMiddle'>Gastgeber werden</span>
            <div
              ref={burgerHelpMenu.containerRef}
              className={`${classes.burgerHelpTrigger} ${burgerHelpMenu.isActive ? classes.burgerHelpTriggerActive : ''}`}
              onMouseEnter={burgerHelpMenu.onMouseEnter}
              onMouseLeave={burgerHelpMenu.onMouseLeave}
              onClick={burgerHelpMenu.onClick}
            >
              <span className='regularMiddle'>Hilfe?</span>
              <HelpMenu
                variant="dropdown"
                panelRef={burgerHelpMenu.panelRef}
                open={burgerHelpMenu.isOpen}
                onClose={burgerHelpMenu.close}
                onMouseEnter={burgerHelpMenu.onMouseEnter}
                onMouseLeave={burgerHelpMenu.onMouseLeave}
              />
            </div>
          </div>
        )}
      </header>
      {!isPhoneSize && !isAuthenticated && (
        <>
          <SignIn open={showSignIn} onClose={handleCloseSignIn} onSwitchToRegister={handleSwitchToRegister} />
          <Register open={showRegister} onClose={handleCloseRegister} onSwitchToSignIn={handleSwitchToSignIn} />
        </>
      )}
      {showProfileMenu && (
        < ProfileMenu
          ref={profileMenuRef}
          setShowProfileMenu={setShowProfileMenu}
          fromProfile={fromProfile}
        />
      )}
    </>
  );
};

export default NavBar;
